import * as echarts from "echarts/core";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import { getUnitCategoryName, type Locale } from "verdi-common/i18n";
import { formatTooltipValue } from "../../format";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import { findNearestReading } from "../hover-values/value-at-time";
import {
    AXIS_LABEL_COLOR,
    HOVER_LABEL_FONT_SIZE_PX,
    HOVER_LABEL_TEXT_COLOR,
    TOOLTIP_LABEL_COLOR,
    TOOLTIP_ROW_HEIGHT_PX,
} from "../layout/plot-geometry";
import { type PreparedSeries, staleAfterMsFor } from "../prepare-series";

/** echarts puts the hovered time on every row of an axis-triggered tooltip; its shared param type doesn't say so. */
type AxisTooltipParams = CallbackDataParams & { axisValue?: number | string | Date };

export type TooltipContext = {
    series: PreparedSeries[];
    getDisplayInfo: GetDisplayInfo;
    showDeviceName: boolean;
    showSensorClusterName: boolean;
    showSensorElementName: boolean;
    locale: Locale;
};

const buildTooltipRowHtml = (preparedSeries: PreparedSeries, timeMs: number, context: TooltipContext): string => {
    const { getDisplayInfo, locale, showDeviceName, showSensorClusterName, showSensorElementName } = context;

    const category = getUnitCategoryName(preparedSeries.unitCategory, locale);
    const reading = findNearestReading(
        preparedSeries.points,
        timeMs,
        preparedSeries.seriesType,
        staleAfterMsFor(preparedSeries.axisUnitCategory),
    );
    // The axis's category, not the series' own: the value on screen was converted to the axis unit.
    const unitSuffix = preparedSeries.axisUnitCategory ? getDisplayInfo(preparedSeries.axisUnitCategory).symbol : "";
    const formattedValue = reading ? [formatTooltipValue(reading.value), unitSuffix].filter(Boolean).join(" ") : "—";

    // Most channels are named after their metric, and the row already leads with that.
    const elementName = preparedSeries.sensorElementName === category ? null : preparedSeries.sensorElementName;

    // Same parts the legend spells out, so a row reads the same in both places. They sit on a second
    // line because a wide tooltip gets shoved over the plot.
    const detail = [
        showDeviceName ? preparedSeries.name : null,
        showSensorClusterName ? preparedSeries.sensorClusterName : null,
        showSensorElementName ? elementName : null,
    ]
        .filter(Boolean)
        .join(" · ");

    // Swatch width plus its gap, so the second line starts under the category.
    const detailIndentPx = 16;
    const detailHtml = detail
        ? `<div style="font-size: ${HOVER_LABEL_FONT_SIZE_PX}px; color: ${AXIS_LABEL_COLOR}; height: ${TOOLTIP_ROW_HEIGHT_PX}px; padding-left: ${detailIndentPx}px;">${echarts.format.encodeHTML(detail)}</div>`
        : "";

    // biome-ignore format: keep tooltip HTML indentation
    const categoryHtml =
        `<div style="display: flex; align-items: center; gap: 6px; height: ${TOOLTIP_ROW_HEIGHT_PX}px;">` +
            `<div style="width: 10px; height: 3px; border-radius: 2px; background-color: ${preparedSeries.color};"></div>` +
            `<div style="color: ${TOOLTIP_LABEL_COLOR};">${echarts.format.encodeHTML(category)}</div>` +
        `</div>`;

    // biome-ignore format: keep tooltip HTML indentation
    return (
        `<div style="display: flex; align-items: center; justify-content: space-between; gap: 16px;">` +
            `<div style="display: flex; flex-direction: column;">${categoryHtml}${detailHtml}</div>` +
            `<div style="font-weight: 600; color: ${HOVER_LABEL_TEXT_COLOR}; font-variant-numeric: tabular-nums;">${formattedValue}</div>` +
        `</div>`
    );
};

/** The time the crosshair snapped to, which is where every row is read. */
function hoveredTimeMs(params: AxisTooltipParams[]): number | null {
    const axisValue = params[0]?.axisValue;
    if (axisValue === undefined) return null;

    const timeMs = axisValue instanceof Date ? axisValue.getTime() : Number(axisValue);
    return Number.isFinite(timeMs) ? timeMs : null;
}

/**
 * One row per series on the panel: swatch, metric, what tells it apart, value. Every series keeps its
 * row at every time — echarts only hands us the series nearest the pointer, and rows that come and go
 * as the pointer moves are hard to read. The crosshair label carries the time.
 */
export function renderTooltipHtml(params: AxisTooltipParams[], tooltipContext: TooltipContext): string {
    const timeMs = hoveredTimeMs(params);
    if (timeMs === null) return "";

    const rows = tooltipContext.series.map((s) => buildTooltipRowHtml(s, timeMs, tooltipContext)).join("");

    return `<div style="display: flex; flex-direction: column;">${rows}</div>`;
}
